import { Building2, CreditCard, Check } from 'lucide-react'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { Skeleton } from '@/components/ui/skeleton'
import { Navigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export function WorkspaceBillingPage() {
    const { currentWorkspace, loading, error, isAdmin } = useWorkspace()

    // Redirect non-admins
    if (!loading && !isAdmin) {
        return <Navigate to="/app/settings/workspace" replace />
    }

    if (loading) {
        return (
            <div className="p-6 md:p-8 w-full animate-fade-in">
                <div className="max-w-[800px] mx-auto">
                    <div className="mb-8">
                        <Skeleton className="h-8 w-48 mb-2" />
                        <Skeleton className="h-4 w-72" />
                    </div>
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="p-6 md:p-8 w-full animate-fade-in">
                <div className="max-w-[800px] mx-auto">
                    <div className="p-6 rounded-lg bg-destructive/10 text-destructive">
                        <h2 className="font-semibold mb-2">Error loading workspace</h2>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        )
    }

    if (!currentWorkspace) {
        return (
            <div className="p-6 md:p-8 w-full animate-fade-in">
                <div className="max-w-[800px] mx-auto">
                    <div className="text-center py-12">
                        <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-2xl font-bold mb-2">No Workspace Selected</h2>
                        <p className="app-page-subtitle">
                            Create a workspace or select one from the sidebar
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    const currentPlan = currentWorkspace.pricingModel?.displayName || 'Free'
    const planFeatures = [
        'Unlimited team members',
        'Basic analytics',
        'Email support',
        'API access',
    ]

    return (
        <div className="p-6 md:p-8 w-full animate-fade-in">
            <div className="max-w-[800px] mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="app-page-title mb-1">Billing & Subscription</h1>
                    <p className="app-page-subtitle">
                        Manage your subscription and billing information
                    </p>
                </div>

                {/* Current Plan */}
                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <CreditCard className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-lg">Current Plan</CardTitle>
                                    <CardDescription>
                                        Your workspace is on the {currentPlan} plan
                                    </CardDescription>
                                </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                currentWorkspace.status === 'paid'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                                {currentPlan}
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-sm font-medium text-foreground mb-2">Plan Features</h4>
                                <ul className="space-y-2">
                                    {planFeatures.map((feature, index) => (
                                        <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Check className="h-4 w-4 text-green-500" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="pt-4 border-t border-border">
                                <Button variant="outline">
                                    Upgrade Plan
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Billing History */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Billing History</CardTitle>
                        <CardDescription>
                            View your past invoices and payment history
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center py-8 text-muted-foreground">
                            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No billing history available</p>
                            <p className="text-sm mt-1">Your invoices will appear here once you upgrade</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
